import React from 'react'

import { XIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import SavedViewFilters from './SavedViewFilters'
import { FilterTypeDescriptor } from './savedViewsTypes'

interface NewViewDialogProps {
  viewName: string
  appliedFilters: { [facetKey: string]: Set<string> }
  groupByCaption?: string
  isSaving?: boolean
  saveError?: string | null
  onCreateClick: () => void
  onCancelClick: () => void
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onIsPublicToggle: () => void
  isPublic: boolean
  isActiveUserAdmin: boolean
  facets?: FilterTypeDescriptor[]
  groupingEnabled?: boolean
}

const NewViewDialog: React.FC<NewViewDialogProps> = ({
  viewName,
  appliedFilters,
  groupByCaption,
  isSaving = false,
  saveError = null,
  onCreateClick,
  onCancelClick,
  onNameChange,
  onIsPublicToggle,
  isPublic,
  isActiveUserAdmin,
  facets,
  groupingEnabled = true,
}) => {
  const isDisabled = isSaving || !viewName.trim()
  return (
    <div className="tw:text-primary tw:bg-background tw:border tw:border-solid tw:border-border tw:rounded-lg tw:shadow-[0px_4px_8px_3px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)] tw:w-[540px] tw:max-w-[540px] tw:h-[540px] tw:max-h-[540px]">
      <div className="tw:flex tw:items-center tw:h-12">
        <div className="tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:w-12 tw:h-12">
          <Button
            variant="ghost"
            size="icon"
            aria-label={Localization.translator.close}
            onClick={onCancelClick}
          >
            <XIcon />
          </Button>
        </div>
        <div className="tw:flex-1 tw:text-title-md tw:text-foreground tw:text-center">
          {Localization.translator.newView}
        </div>
        <div
          className={classList({
            'tw:shrink-0 tw:px-3 tw:py-2.5 tw:cursor-pointer': true,
            'tw:opacity-50': isDisabled,
          })}
          onClick={isDisabled ? undefined : onCreateClick}
          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
        >
          {isSaving ? 'Saving...' : Localization.translator.create}
        </div>
      </div>
      <div className="tw:h-[calc(100%-48px)] tw:overflow-y-scroll overflow-hidden-scrollbar">
        {saveError && (
          <div className="tw:px-4 tw:py-2">
            <div className="tw:text-body-sm tw:text-destructive">
              {saveError}
            </div>
          </div>
        )}
        <div className="tw:pt-3 tw:px-4">
          <StruxhubInput
            label={Localization.translator.viewName}
            isRequiredTextHidden={true}
            value={viewName}
            onChange={onNameChange}
            isDisabled={isSaving}
          />
        </div>
        <div className="tw:pr-4 tw:pb-3 tw:pl-6 tw:flex tw:items-center tw:[&>*]:flex-1 tw:items-start tw:gap-[8px]">
          <div className="tw:my-2.5 tw:flex tw:items-center tw:[&>*]:flex-1 as-stretch tw:text-body-md tw:text-foreground">
            {Localization.translator.shareToProject}
          </div>
          <Switch
            className="tw:h-[18px] tw:flex-none tw:p-0 tw:m-auto"
            checked={isPublic}
            disabled={!isActiveUserAdmin || isSaving}
            onCheckedChange={onIsPublicToggle}
          />
        </div>
        <SavedViewFilters
          appliedFilters={appliedFilters}
          groupBy={groupByCaption}
          facets={facets}
          groupingEnabled={groupingEnabled}
          className="tw:flex-1 tw:flex tw:flex-col tw:items-start as-stretch tw:mr-1"
        />
      </div>
    </div>
  )
}

export default NewViewDialog
